// AVATAR ÚNICO DE PERSONA — foto o inicial, con la insignia de adeudo encima.
//
// 🚨 EL PUNTO DE ESTE COMPONENTE: que la insignia SIGA al alumno a todas partes.
// Antes cada pantalla dibujaba su propio círculo a mano (33 sitios en 24 archivos), que es
// exactamente por lo que en Kydemy el símbolo de "debe" aparece en unas vistas y en otras no.
// Si añades una pantalla nueva con personas, USA ESTE COMPONENTE — no copies un <img rounded-full>.
//
// La insignia sale sola: el componente consulta el mapa de banderas (DebtFlagsContext), que se
// carga una vez para toda la consola. Quien renderiza no tiene que saber nada de adeudos.
import { useDebtFlag, useUserDebtFlag } from '../context/DebtFlagsContext';
import './PersonAvatar.css';
import { photoUrl } from '../features/members/api';

interface Props {
  /** contact_id (== account_id en la consola). Sin él no hay foto ni insignia.
   *
   *  🚨 SOLO ALUMNOS. Pasar `null` para profesores/staff: sus ids salen de `users`, no de
   *  `contacts`, y en las vistas que hacen UNION de ambos (cumpleaños) los números CHOCAN —
   *  un profe con users.id 39055 heredaría el adeudo del alumno contacts.id 39055. */
  contactId?: number | null;
  /** users.id de un maestro / persona del equipo: su moneda sale de los FIADOS de vitrina
   *  (mapa aparte, 29-sep-2026). Se pasa junto con `photoSrc={teacherPhotoUrl(id)}` y SIN contactId. */
  userId?: number | null;
  hasPhoto?: boolean | number | null;
  /** URL de foto alterna: profesores (`teacherPhotoUrl`) o con cache-buster (`?v=`).
   *  Si se pasa, manda sobre la foto derivada de `contactId`. */
  photoSrc?: string | null;
  /** Para la inicial del placeholder y el alt/title. */
  name?: string | null;
  lastName?: string | null;
  /** Clases de tamaño Tailwind. La insignia se escala sola (va en % del cuadro). */
  size?: string;
  /** Tamaño del texto de la inicial (acompaña a `size`). */
  initialClass?: string;
  /** `rounded-full` por defecto; las tarjetas grandes de asistencia usan esquinas propias. */
  rounded?: string;
  /** Clases extra SOLO para la <img> (p. ej. el `grayscale` del ausente en pasar lista). */
  imgClass?: string;
  /** Esquina de la moneda. 'br' por defecto; 'tr' cuando abajo-derecha ya está ocupada
   *  (la ficha del alumno tiene ahí el botón de subir foto). */
  badgeCorner?: 'br' | 'tr';
  className?: string;
  /** Color del anillo que despega la insignia del fondo. Por defecto el de tarjeta/panel
   *  (`surface`), que es donde vive la inmensa mayoría de los avatares. En un fondo de página
   *  pasar `fill-canvas`. */
  ringClass?: string;
  /** Apagar la insignia en contextos donde exponer el adeudo sería indebido — p. ej. pantallas
   *  proyectadas en el salón (ScreenSaverMode), donde la verían otros alumnos. */
  showDebt?: boolean;
  title?: string;
}

/** Etiqueta legible del adeudo, para el tooltip nativo. */
function tituloAdeudo(total: number, meses: string[], vencido: boolean): string {
  const monto = total.toLocaleString('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 });
  const cuando = meses.length ? ` · ${meses.join(', ')}` : '';
  return `${vencido ? 'Adeudo vencido' : 'Adeudo pendiente'}: ${monto}${cuando}`;
}

export default function PersonAvatar({
  contactId, userId, hasPhoto, photoSrc, name, lastName, size = 'h-9 w-9', initialClass = 'text-xs',
  rounded = 'rounded-full', imgClass = '', badgeCorner = 'br',
  className = '', ringClass = 'fill-surface', showDebt = true, title,
}: Props) {
  const flagAlumno = useDebtFlag(showDebt ? contactId : null);
  const flagEquipo = useUserDebtFlag(showDebt && !contactId ? userId : null);
  const flag = flagAlumno ?? flagEquipo;
  const inicial = (name || '').trim()[0]?.toUpperCase() || '?';
  const nombre = [name, lastName].filter(Boolean).join(' ').trim();
  const vencido = flag?.level === 'overdue';

  const src = photoSrc ?? (contactId ? photoUrl(contactId) : null);
  const foto = hasPhoto && src
    ? <img src={src} alt="" loading="lazy"
        className={`h-full w-full object-cover ${rounded} ${imgClass}`} />
    : <span className={`flex h-full w-full items-center justify-center ${rounded} bg-accent/15 font-semibold text-accent-ink ${initialClass}`}>{inicial}</span>;

  // El tooltip del contenedor: nombre + adeudo si lo hay. `title` explícito manda.
  const tip = title ?? ([nombre, flag ? tituloAdeudo(flag.total, flag.months, vencido) : '']
    .filter(Boolean).join(' — ') || undefined);

  return (
    <span className={`lm-avatar relative inline-block shrink-0 ${size} ${className}`} title={tip}>
      {foto}
      {flag && (
        // MONEDA en la esquina. El TAMAÑO ya no vive aquí: lo decide `.lm-debt-badge` en el CSS
        // con container queries, porque un porcentaje fijo se leía bien en la ficha (64px) y
        // desaparecía en la lista (36px). Medido en producción el 7-ago: 18px allí, invisible.
        // Se mueven las DOS, con intensidades distintas: el ámbar late (+16%, 2.2s, halo tenue) y
        // el rojo golpea (+28%, 1.35s, halo fuerte). Detalle y números en PersonAvatar.css.
        <span
          className={`lm-debt-badge pointer-events-none absolute -right-1 ${badgeCorner === 'tr' ? '-top-1' : '-bottom-1'} block ${vencido ? 'lm-debt-pulse' : 'lm-debt-breathe'}`}
          aria-hidden="true"
        >
          {/* Halo: hermano ANTES del svg y ambos posicionados → pinta detrás sin z-index negativo
              (un z-index:-1 se metería debajo de la foto, que no crea contexto de apilamiento). */}
          {vencido
            ? <span className="lm-debt-halo absolute inset-0 rounded-full bg-red-600" />
            : <span className="lm-debt-halo-soft absolute inset-0 rounded-full bg-amber-500" />}
          <svg viewBox="0 0 24 24" className="relative h-full w-full">
            {/* anillo del color del fondo: despega la moneda de la foto */}
            <circle cx="12" cy="12" r="12" className={ringClass} />
            <circle cx="12" cy="12" r="9.5" className={vencido ? 'fill-red-600' : 'fill-amber-500'} />
            <text x="12" y="16.6" textAnchor="middle" fontSize="15" fontWeight="700" fill="#fff"
              fontFamily="system-ui, sans-serif">$</text>
          </svg>
        </span>
      )}
      {/* Lectores de pantalla: la moneda es decorativa, el dato va en texto. */}
      {flag && <span className="sr-only">{tituloAdeudo(flag.total, flag.months, vencido)}</span>}
    </span>
  );
}
